'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MapPin } from 'lucide-react';
import { css, cx } from '@/styled-system/css';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import LocationSearchModal from './LocationSearchModal';
import { formatAreaLabel } from './useLocationSuggestions';

/*
 * Matches the Combobox trigger's pill so the bar looks identical either side of
 * `lg`. Three fully inlined objects, not one built from a shared base: Panda
 * extracts `css` statically and `c11nLint/no-dynamic-css-args` rejects spreads.
 */
const triggerLg = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tighter',
  width: '100%',
  minWidth: 0,
  minBlockSize: 'tapArea.mobile.default',
  borderRadius: 'pill',
  backgroundColor: 'bg.action.neutral.alt.default',
  cursor: 'pointer',
  textAlign: 'start',
  paddingInline: 'default',
  paddingBlock: 'tighter',
  // Constellation's own focus treatment, not the browser's default outline —
  // otherwise this field focuses blue while the combobox next to it focuses
  // teal. `shadows.focus` is the same token c11n's own controls use.
  _focusVisible: {
    outline: 'none',
    boxShadow: 'focus',
  },
});

const triggerSm = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tighter',
  width: '100%',
  minWidth: 0,
  minBlockSize: 'tapArea.mobile.default',
  borderRadius: 'pill',
  backgroundColor: 'bg.action.neutral.alt.default',
  cursor: 'pointer',
  textAlign: 'start',
  paddingBlock: 'tighter',
  // Constellation's own focus treatment, not the browser's default outline —
  // otherwise this field focuses blue while the combobox next to it focuses
  // teal. `shadows.focus` is the same token c11n's own controls use.
  _focusVisible: {
    outline: 'none',
    boxShadow: 'focus',
  },
});

const triggerCompact = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tighter',
  width: '100%',
  minWidth: 0,
  minBlockSize: 'tapArea.mobile.default',
  borderRadius: 'pill',
  backgroundColor: 'bg.action.neutral.alt.default',
  cursor: 'pointer',
  textAlign: 'start',
  paddingBlock: 'none',
  // Constellation's own focus treatment, not the browser's default outline —
  // otherwise this field focuses blue while the combobox next to it focuses
  // teal. `shadows.focus` is the same token c11n's own controls use.
  _focusVisible: {
    outline: 'none',
    boxShadow: 'focus',
  },
});

const label = css({
  textStyle: 'body',
  color: 'text.neutral',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const srOnly = css({ srOnly: true });
const labelBold = css({ fontWeight: 'bold' });
const placeholder = css({ color: 'text.subtle', fontWeight: 'regular' });

/** Mirrors `LocationCombobox`, so the bar can swap one for the other. */
export type LocationInputProps = {
  /** Currently selected area, shown as the field's value. */
  area: AreaResponse | AutocompleteResult | null;
  /** IP-derived city for the "Near Me" affordance in the dialog. */
  nearMeArea?: AreaResponse | null;
  /** Called when the user picks a suggestion or "Near Me". */
  onSelect: (result: AutocompleteResult) => void;
  /** Called when Enter should run the search for a suggestion right away. */
  onEnterSearch: (result: AutocompleteResult) => void;
  /** Called on every keystroke so the parent can cancel recent-search seeding. */
  onUserInput: () => void;
  size?: 'sm' | 'lg';
  compact?: boolean;
  testIdSuffix?: string;
};

/**
 * The location field below `lg`: a button that opens the full-screen search
 * (HP-8998), styled to match the Combobox trigger it stands in for.
 *
 * Deliberately not a Combobox. Tapping this opens a dialog, so the element is a
 * button with `aria-haspopup="dialog"` — not an input reporting
 * `role="combobox"` for a listbox it will never show. It also means no
 * floating-ui, no suggestion fetching and no options work runs in the bar at
 * all; that lives in the dialog, which only mounts once opened.
 */
export default function LocationInput({
  area,
  nearMeArea,
  onSelect,
  onEnterSearch,
  onUserInput,
  size = 'sm',
  compact = false,
  testIdSuffix,
}: LocationInputProps) {
  const tSearch = useTranslations('search');
  const areaLabel = formatAreaLabel(area);
  const triggerCss = size === 'lg' ? triggerLg : compact ? triggerCompact : triggerSm;

  // The dialog is this field's own popup, the way the dropdown belongs to
  // LocationCombobox — the bar shouldn't have to know either exists. Mounted
  // only while open so dismissing discards its draft query.
  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <button
        // Inside ListingSearch's <form>, so it must not be the submit button.
        type="button"
        className={triggerCss}
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        data-testid={testIdSuffix ? `location-search-trigger-${testIdSuffix}` : 'location-search-trigger'}
      >
        <LucideIcon icon={MapPin} size={size === 'lg' ? 'md' : 'sm'} />
        {/* Named by its content, not `aria-label` — that would replace the
            visible text, so the accessible name would never carry the selected
            area (WCAG 2.5.3 Label in Name, and the Combobox this stands in for
            announced its value). */}
        <span className={srOnly}>{tSearch('searchByLocation')}</span>
        <span className={cx(label, areaLabel ? labelBold : placeholder)}>
          {areaLabel || tSearch('findApartmentsIn')}
        </span>
      </button>
      {isOpen ? (
        <LocationSearchModal
          onClose={close}
          nearMeArea={nearMeArea}
          onSelect={onSelect}
          onEnterSearch={onEnterSearch}
          onUserInput={onUserInput}
          // `lg` is only ever the homepage hero, same derivation the inline
          // field uses.
          analyticsSurface={size === 'lg' ? 'homepage' : 'search'}
        />
      ) : null}
    </>
  );
}
